import { useDocumentStore } from '@/stores/documentStore'
import { createFileRoute, useLocation, useParams } from '@tanstack/react-router'
import DocumentEditing from '@/components/document-edit/DocumentEditing'
import { Button } from '@/components/ui/button'
import { v4 as uuidv4 } from 'uuid'
import { PlusIcon } from 'lucide-react'
import ErrorDocumentEdit from '@/components/document-edit/ErrorDocumentEdit'
import { LoadingDocumentEditContent } from '@/components/loading-ui/LoadingDocument'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useEffect, useRef, Suspense, use, useMemo, memo } from 'react'
import { useImageFromReferenceDocumentId } from '@/hooks/useImageFromReferenceDocumentId'
import type { IllustratedItem } from '@/data/entities'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/',
)({
  component: memo(RouteComponent),
  errorComponent: ({ error }) => {
    console.error(error)
    return <ErrorDocumentEdit error={error} />
  },
  pendingComponent: () => <LoadingDocumentEditContent />,
})

// Component for rendering an illustrated item with async image loading
function IllustratedItemComponent({
  item,
  fromId,
  index,
}: {
  item: IllustratedItem
  fromId: (id: string) => Promise<string | undefined>
  index: number
}) {
  // Memoize the promise to ensure React's use() hook can track it properly
  // Always create a promise (even if undefined) so use() can be called unconditionally
  const promise = useMemo(
    () => (item.image ? fromId(item.image) : Promise.resolve(undefined)),
    [item.image, fromId],
  )
  const imageUrl = use(promise)

  return (
    <TableRow className="divide-x">
      <TableCell className="w-fit pr-2 text-center font-bold whitespace-nowrap">
        {index + 1}
      </TableCell>
      <TableCell className="pr-4">
        <p className="whitespace-pre-line">{item.text}</p>
      </TableCell>
      <TableCell className="flex items-center justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Reference document image"
            className="h-16 md:h-24"
          />
        ) : (
          <span className="text-muted-foreground">no image</span>
        )}
      </TableCell>
    </TableRow>
  )
}

function RouteComponent() {
  // TODO: handle conditional for edit access
  const location = useLocation()
  const { projectId } = useParams({
    from: '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/',
  })
  const fromId = useImageFromReferenceDocumentId(projectId!)
  const documentData = useDocumentStore((state) => state.documentData)
  const isEditing = useDocumentStore((state) => state.isEditing)
  const setIsEditing = useDocumentStore((state) => state.setEditing)
  const addSection = useDocumentStore((state) => state.addSection)
  const refs = useRef<{ [key: string]: HTMLElement }>({})

  useEffect(() => {
    if (!location.hash) return
    const sectionId = location.hash
    const section = refs.current[sectionId]
    if (!section) return
    setTimeout(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 200)
  }, [location.hash])

  const handleAddSection = () => {
    setIsEditing(true)
    addSection({
      id: uuidv4(),
      title: '',
      body: '',
      order: documentData.content?.sections?.length || 0,
    })
  }

  return documentData.content?.sections &&
    documentData.content?.sections.length > 0 ? (
    isEditing ? (
      <div className="flex flex-col gap-4">
        <DocumentEditing documentData={documentData} projectId={projectId} />
      </div>
    ) : (
      <section className="flex flex-col pb-12 md:gap-6">
        {documentData.content?.sections?.map((section) => (
          <div
            id={section.id}
            key={section.id}
            ref={(el) => {
              if (el) {
                refs.current[section.id] = el
              }
            }}
            className={cn(
              'flex flex-col gap-2 [&:not(:first-child)]:pt-6',
              location.hash === section.id && 'border-primary bg-primary/10',
            )}
          >
            <h2 className="text-base font-semibold sm:text-xl">
              {section.title}
            </h2>
            <Separator />
            <p className="whitespace-pre-line">{section.body}</p>
            {section.subText && section.subText.length > 0 && (
              <ul className="pl-4">
                {section.subText.map((subText) => (
                  <li key={subText} className="list-disc">
                    <p className="whitespace-pre-line">{subText}</p>
                  </li>
                ))}
              </ul>
            )}
            {section.illustratedItems &&
              section.illustratedItems.length > 0 && (
                <Table className="border">
                  <TableHeader>
                    <TableRow className="bg-muted divide-x">
                      <TableHead className="w-[40px] text-center font-bold whitespace-nowrap">
                        #
                      </TableHead>
                      <TableHead className="pr-4">
                        <p className="font-bold whitespace-pre-line">Name</p>
                      </TableHead>
                      <TableHead className="pl-4">
                        <p className="font-bold whitespace-pre-line">Image</p>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {section.illustratedItems.map((item, index) => (
                      <Suspense
                        key={item.image || `illustrated-item-${index}`}
                        fallback={
                          <TableRow className="divide-x">
                            <TableCell className="text-center font-bold whitespace-nowrap">
                              {index + 1}
                            </TableCell>
                            <TableCell className="pr-4">
                              <p className="whitespace-pre-line">{item.text}</p>
                            </TableCell>
                            <TableCell className="pl-4">
                              <div className="bg-muted h-8 w-8 animate-pulse" />
                            </TableCell>
                          </TableRow>
                        }
                      >
                        <IllustratedItemComponent
                          item={item}
                          fromId={fromId}
                          index={index}
                        />
                      </Suspense>
                    ))}
                  </TableBody>
                </Table>
              )}
          </div>
        ))}
      </section>
    )
  ) : (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <p className="text-copy-secondary text-center">No Document Data</p>
      <Button variant="ghost" onClick={handleAddSection}>
        <PlusIcon />
        Add Section
      </Button>
    </div>
  )
}
